import { OrganizationService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/organization_connect";
import { UserService } from "@buf/brevdev_devplane.connectrpc_es/devplaneapi/v1/user_connect";
import { OrganizationAccessLevel } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_access_pb";
import type { ExternalNode } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/external_node_pb";
import { ComputeResourceType } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/organization_pb";
import type { CallOptions } from "@connectrpc/connect";
import type { AdminOrganizationType } from "components/Admin/types";
import type { Environment } from "components/Environment/utils/types";
import { createConnectClient } from "../../connect";
import { mapEnvironment } from "../environments/mappers";
import { mapAdminOrganization, mapOrganization } from "./mappers";

const organizationClient = createConnectClient(OrganizationService);
const userClient = createConnectClient(UserService);

const organizationListAttachedDataOptions = {
  includeNgcEnablements: true,
  program: true,
};

const organizationGetAttachedDataOptions = {
  includeNgcEnablements: true,
  program: true,
};

const userOrganizationAccessAttachedDataOptions = {
  organizationAccesses: true,
};

const adminOrganizationAttachedDataOptions = {
  program: true,
  reservedInstancePools: true,
};

const adminOrganizationPageAttachedDataOptions = {
  program: true,
};

const adminOrganizationDefaultPageOrdering = {
  orderBy: "create_time",
  ordering: "desc" as const,
};

const organizationComputeResourceOrdering = {
  orderBy: "create_time",
  ordering: "desc" as const,
};

const adminOrganizationPageSizes = new Set([10, 25, 50, 100]);
const organizationComputeResourcePageSize = 100;
const organizationMemberPageSize = 100;

export type OrganizationComputeResource =
  | { type: "cloud"; environment: Environment }
  | { type: "local"; node: ExternalNode };

export interface OrganizationComputeResourceFilters {
  type?: "all" | "cloud" | "local";
  createdByUserId?: string;
}

export type OrganizationMember = {
  id: string;
  name: string;
  email: string;
};

const getOrganizationComputeResourceType = (
  type: OrganizationComputeResourceFilters["type"],
) => {
  if (type === "cloud") return ComputeResourceType.ENVIRONMENT;
  if (type === "local") return ComputeResourceType.EXTERNAL_NODE;
  return ComputeResourceType.UNSPECIFIED;
};

export async function organizationHasComputeResources(
  organizationId: string,
  options?: CallOptions,
): Promise<boolean> {
  const response = await organizationClient.listOrganizationComputeResources(
    {
      organizationId,
      pageParams: { pageSize: 1, pageToken: "" },
      options: {
        orderOptions: organizationComputeResourceOrdering,
        environmentOptions: { instance: true },
        externalNodeOptions: { excludeConnectivityInfo: true },
      },
    },
    options,
  );

  return response.items.length > 0;
}

export async function listOrganizationMembers(
  organizationId: string,
  options?: CallOptions,
): Promise<OrganizationMember[]> {
  const members: OrganizationMember[] = [];
  const seenPageTokens = new Set<string>();
  let pageToken = "";

  do {
    const response = await organizationClient.listOrganizationMembers(
      {
        organizationId,
        pageParams: {
          pageSize: organizationMemberPageSize,
          pageToken,
        },
      },
      options,
    );

    members.push(
      ...response.items.map((member) => ({
        id: member.userId,
        name: member.displayName || member.defaultEmail || member.userId,
        email: member.defaultEmail,
      })),
    );

    if (!response.nextPageToken || seenPageTokens.has(response.nextPageToken)) {
      break;
    }
    seenPageTokens.add(response.nextPageToken);
    pageToken = response.nextPageToken;
  } while (pageToken);

  return members.sort((first, second) => first.name.localeCompare(second.name));
}

export async function listOrganizationComputeResources(
  organizationId: string,
  filters: OrganizationComputeResourceFilters = {},
  options?: CallOptions,
  excludeConnectivityInfo: boolean = true,
): Promise<OrganizationComputeResource[]> {
  const resources: OrganizationComputeResource[] = [];
  const seenPageTokens = new Set<string>();
  let pageToken = "";

  do {
    const response = await organizationClient.listOrganizationComputeResources(
      {
        organizationId,
        pageParams: {
          pageSize: organizationComputeResourcePageSize,
          pageToken,
        },
        options: {
          orderOptions: organizationComputeResourceOrdering,
          environmentOptions: {
            instance: true,
            instanceType: true,
            ingressGateways: true,
          },
          externalNodeOptions: { excludeConnectivityInfo },
          type: getOrganizationComputeResourceType(filters.type),
          createdByUserId: filters.createdByUserId ?? "",
        },
      },
      options,
    );

    for (const item of response.items) {
      if (item.value.case === "environment") {
        resources.push({
          type: "cloud",
          environment: mapEnvironment(item.value.value),
        });
      } else if (item.value.case === "externalNode") {
        resources.push({ type: "local", node: item.value.value });
      }
    }

    if (!response.nextPageToken || seenPageTokens.has(response.nextPageToken)) {
      break;
    }
    seenPageTokens.add(response.nextPageToken);
    pageToken = response.nextPageToken;
  } while (pageToken);

  return resources;
}

export interface CreateOrganizationForUserInput {
  name: string;
  ownerUserId: string;
  labels?: Record<string, string>;
  programId?: string;
}

export interface OrganizationCreditsInput {
  amountUSD: string;
  organizationId: string;
}

export interface TransferOwnershipInput {
  organizationId: string;
  ownerUserId: string;
}

export interface UpdateAdminOrganizationInput {
  organizationId: string;
  name?: string;
  labels?: Record<string, string>;
  programId?: string;
}

export interface ListAdminOrganizationsInput {
  hasAllLabels?: Record<string, string>;
  hasOneOfLabels?: Record<string, { value: string[] }>;
  ids?: string[];
  ncaIds?: string[];
  ngcOrgIds?: string[];
  programIds?: string[];
  orderBy?: string;
  ordering?: "asc" | "desc";
}

export interface ListAdminOrganizationsPageInput extends ListAdminOrganizationsInput {
  pageSize: number;
  pageToken?: string;
}

export interface AdminOrganizationPage {
  items: AdminOrganizationType[];
  nextPageToken: string;
}

export interface AdminOrganizationOwner {
  userId: string;
  displayName: string;
  defaultEmail: string;
}

function getAdminOrganizationListOptions(
  input: ListAdminOrganizationsInput,
  attachedDataOptions: {
    program: boolean;
    reservedInstancePools?: boolean;
  } = adminOrganizationAttachedDataOptions,
) {
  const { orderBy, ordering } = input;
  return {
    hasAllLabels: input.hasAllLabels ?? {},
    hasOneOfLabels: input.hasOneOfLabels ?? {},
    ids: input.ids ?? [],
    ncaIds: input.ncaIds ?? [],
    ngcOrgIds: input.ngcOrgIds ?? [],
    programIds: input.programIds ?? [],
    ...(!orderBy && !ordering ? {} : { orderOptions: { orderBy, ordering } }),
    attachedDataOptions,
  };
}

export async function adminListOrganizationsForUser(userId: string) {
  const userResponse = await userClient.getUser({
    userId,
    attachedDataOptions: userOrganizationAccessAttachedDataOptions,
  });
  const organizationIds =
    userResponse.user?.organizationAccesses.map(
      (access) => access.organizationId,
    ) ?? [];

  if (organizationIds.length === 0) {
    return [];
  }

  const organizationResponse = await organizationClient.listOrganization({
    options: {
      ids: organizationIds,
      includeNgcEnablements: true,
      attachedDataOptions: organizationListAttachedDataOptions,
    },
  });
  return organizationResponse.items.map(mapOrganization);
}

export async function getOrganization(organizationId: string) {
  const response = await organizationClient.getOrganization({
    organizationId,
    attachedDataOptions: organizationGetAttachedDataOptions,
  });
  return response.organization
    ? mapOrganization(response.organization)
    : undefined;
}

export async function createOrganization(name: string) {
  const response = await organizationClient.createOrganization({
    displayName: name,
  });
  return response.organization
    ? mapOrganization(response.organization)
    : undefined;
}

export async function createOrganizationForUser({
  name,
  ownerUserId,
  labels,
  programId,
}: CreateOrganizationForUserInput) {
  const response = await organizationClient.createOrganizationForUser({
    displayName: name,
    ownerUserId,
    labels: labels ?? {},
    programId: programId ?? "",
  });
  return response.organization
    ? mapOrganization(response.organization)
    : undefined;
}

export async function deleteOrganization(organizationId: string) {
  await organizationClient.deleteOrganization({ organizationId });
}

export async function transferOwnership({
  organizationId,
  ownerUserId,
}: TransferOwnershipInput) {
  await organizationClient.transferOwnership({
    organizationId,
    ownerUserId,
  });
}

export async function addCreditsToOrganization(
  input: OrganizationCreditsInput,
) {
  return organizationClient.addCreditsToOrganization({
    organizationId: input.organizationId,
    amountUsd: input.amountUSD,
  });
}

export async function removeCreditsFromOrganization(
  input: OrganizationCreditsInput,
) {
  return organizationClient.removeCreditsFromOrganization({
    organizationId: input.organizationId,
    amountUsd: input.amountUSD,
  });
}

export async function getAdminOrganization(
  organizationId: string,
  options?: CallOptions,
) {
  const response = await organizationClient.getOrganization(
    {
      organizationId,
      attachedDataOptions: adminOrganizationAttachedDataOptions,
    },
    options,
  );
  if (!response.organization) {
    throw new Error("organization was not returned");
  }
  return mapAdminOrganization(response.organization);
}

export async function updateAdminOrganization({
  organizationId,
  name,
  labels,
  programId,
}: UpdateAdminOrganizationInput) {
  const response = await organizationClient.updateOrganization({
    organizationId,
    ...(name ? { displayName: name } : {}),
    ...(labels === undefined ? {} : { labels }),
    ...(programId ? { programId } : {}),
  });
  if (!response.organization) {
    throw new Error("organization was not returned");
  }
  return mapAdminOrganization(response.organization);
}

export async function listAdminOrganizations(
  input: ListAdminOrganizationsInput = {},
  options?: CallOptions,
) {
  const response = await organizationClient.listOrganization(
    {
      options: getAdminOrganizationListOptions(input),
    },
    options,
  );
  return response.items.map(mapAdminOrganization);
}

export async function listAdminOrganizationsPage(
  input: ListAdminOrganizationsPageInput,
  options?: CallOptions,
): Promise<AdminOrganizationPage> {
  if (!adminOrganizationPageSizes.has(input.pageSize)) {
    throw new RangeError(
      "admin organization page size must be 10, 25, 50, or 100",
    );
  }

  const response = await organizationClient.listOrganization(
    {
      pageParams: {
        pageSize: input.pageSize,
        pageToken: input.pageToken ?? "",
      },
      options: getAdminOrganizationListOptions(
        { ...adminOrganizationDefaultPageOrdering, ...input },
        adminOrganizationPageAttachedDataOptions,
      ),
    },
    options,
  );
  return {
    items: response.items.map(mapAdminOrganization),
    nextPageToken: response.nextPageToken,
  };
}

export async function getAdminOrganizationOwner(
  organizationId: string,
  options?: CallOptions,
): Promise<AdminOrganizationOwner | undefined> {
  const response = await organizationClient.listOrganizationMembers(
    {
      organizationId,
      pageParams: { pageSize: 1, pageToken: "" },
      options: {
        accesses: [
          {
            organizationId,
            accessLevel: OrganizationAccessLevel.OWNER,
          },
        ],
      },
    },
    options,
  );
  const owner = response.items[0];
  return owner
    ? {
        userId: owner.userId,
        displayName: owner.displayName,
        defaultEmail: owner.defaultEmail,
      }
    : undefined;
}

export async function listAdminOrganizationsForSearch(options?: CallOptions) {
  const response = await organizationClient.listOrganization(
    {
      options: {
        attachedDataOptions: {},
      },
    },
    options,
  );
  return response.items.map(mapAdminOrganization);
}

export async function listAdminOrganizationsByIds(
  organizationIds: string[],
  options?: CallOptions,
) {
  if (organizationIds.length === 0) return [];

  const response = await organizationClient.listOrganization(
    {
      pageParams: { pageSize: organizationIds.length },
      options: {
        ids: organizationIds,
        attachedDataOptions: {},
      },
    },
    options,
  );
  return response.items.map(mapAdminOrganization);
}
